import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H1,
  H3,
  Text,
  InputGroup,
  Intent,
  Icon,
  Tooltip,
  Position,
  NonIdealState,
  Spinner,
  FormGroup,
  ControlGroup,
  Alert,
} from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import BranchService from "../../../micro/services/BranchService";
import AuditService from "../../../micro/services/AuditService";
import NewMultiSelector from "../../../micro/components/NewMultiSelector";
import Modal from "../../../micro/components/Modal";
import AuditLogViewer from "../../../micro/components/AuditLogViewer";

// Set AG Grid license
LicenseManager.setLicenseKey("");

class SurgeryGroupMaster extends React.Component {
  state = {
    // Data
    groups: [],
    branches: [],
    sites: [],

    // Loading states
    isLoading: true,
    isSaving: false,

    // Grid state
    gridApi: null,
    columnApi: null,
    quickFilterText: "",

    // Form state for adding new group
    newGroup: {
      groupName: "",
      sites: [],
      branches: [],
    },

    // UI state for new group modal
    showNewGroupModal: false,

    // Edit modal state
    isEditModalOpen: false,
    editingGroup: null,

    // Audit log state
    showAuditLogs: false,

    // Delete confirmation state
    showDeleteConfirmation: false,
    groupToDelete: null,
  };

  componentDidMount() {
    this.loadData();
  }

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  columnDefs = [
    {
      headerName: "Group Name",
      field: "groupName",
      width: 200,
      filter: "agTextColumnFilter",
      pinned: "left",
      editable: false,
    },
    {
      headerName: "Branches",
      field: "branches",
      width: 300,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (!params.value || params.value.length === 0) {
          return "No branches";
        }

        if (params.value.length <= 2) {
          return params.value.join(", ");
        }

        return params.value.slice(0, 2).join(", ") + ` (+${params.value.length - 2} more)`;
      },
      editable: false,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = document.createElement("button");
        const deleteBtn = document.createElement("button");

        editBtn.innerHTML = "✏️";
        editBtn.style.marginRight = "5px";
        editBtn.style.padding = "4px 8px";
        editBtn.style.border = "1px solid #137cbd";
        editBtn.style.backgroundColor = "#137cbd";
        editBtn.style.color = "white";
        editBtn.style.borderRadius = "3px";
        editBtn.style.cursor = "pointer";
        editBtn.onclick = () => this.editGroup(params.data);

        deleteBtn.innerHTML = "🗑️";
        deleteBtn.style.padding = "4px 8px";
        deleteBtn.style.border = "1px solid #db3737";
        deleteBtn.style.backgroundColor = "#db3737";
        deleteBtn.style.color = "white";
        deleteBtn.style.borderRadius = "3px";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.onclick = () => this.showDeleteConfirmation(params.data);

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);

        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  loadData = async () => {
    try {
      this.setState({ isLoading: true });
      cogoToast.loading("Loading surgery group data...");

      // Load groups, branches, and sites in parallel
      await Promise.all([this.loadGroups(), this.loadBranches(), this.loadSites()]);

      cogoToast.success("Data loaded successfully");
    } catch (err) {
      cogoToast.error("Failed to load data. Please try again.");
      console.error("Detailed error loading data:", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      const branchNames = await BranchService.getBranchNames(this.props.authStore.prefix);
      this.setState({ branches: branchNames });
      console.log("✅ DEBUG: Branches loaded successfully:", branchNames.length);
    } catch (err) {
      console.error("Failed to load branches:", err);
      // Fallback to dummy branches
      this.setState({
        branches: ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"],
      });
      console.warn("⚠️ DEBUG: Using fallback branches due to API error");
    }
  };

  loadSites = async () => {
    try {
      console.log("🔍 DEBUG: Loading sites from getAllSites API");
      const response = await apiCallPost(`/rai/${this.props.authStore.prefix}/getAllSites`, {});
      this.setState({ sites: response || [] });
      console.log("✅ DEBUG: Sites loaded successfully:", (response || []).length);
    } catch (err) {
      console.error("Failed to load sites:", err);
      // Fallback to dummy sites
      this.setState({
        sites: [
          { _id: "1", siteName: "Newtown Branch" },
          { _id: "2", siteName: "Park Street Branch" },
          { _id: "3", siteName: "Salt Lake Branch" },
          { _id: "4", siteName: "Howrah Branch" },
        ],
      });
      console.warn("⚠️ DEBUG: Using fallback sites due to API error");
    }
  };

  loadGroups = async () => {
    try {
      console.log("🔍 DEBUG: Loading surgery groups from API");
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/getAll`, {});
      this.setState({ groups: response || [] });
      console.log("✅ DEBUG: Surgery groups loaded successfully:", (response || []).length);
    } catch (err) {
      console.error("Failed to load surgery groups:", err);
      // Fallback to dummy data if API fails
      // this.initializeDummyData();
      console.warn("⚠️ DEBUG: Using dummy data due to API error");
    }
  };

  initializeDummyData = () => {
    const dummyGroups = [
      {
        _id: "1",
        groupName: "Glaucoma",
        branches: ["Newtown Branch", "Park Street Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "2",
        groupName: "Oculoplasty",
        branches: ["Salt Lake Branch", "Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "3",
        groupName: "Vitro Retinal Surgery",
        branches: ["Newtown Branch", "Salt Lake Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "4",
        groupName: "Vitro Retinal Surgery - Scleral Buckling",
        branches: ["Park Street Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "5",
        groupName: "Vitro Retinal Surgery - Silicone Oil Removal",
        branches: ["Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "6",
        groupName: "Vitro Retinal Surgery - Vitrectomy",
        branches: ["Newtown Branch", "Park Street Branch", "Salt Lake Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "7",
        groupName: "Vitro Retinal Surgery - Injection",
        branches: ["Howrah Branch", "Newtown Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "8",
        groupName: "Cornea",
        branches: ["Park Street Branch", "Salt Lake Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "9",
        groupName: "LASIK",
        branches: ["Newtown Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "10",
        groupName: "Misc",
        branches: ["Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "11",
        groupName: "Eye Bank",
        branches: ["Salt Lake Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "12",
        groupName: "Miscellaneous",
        branches: ["Park Street Branch", "Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "13",
        groupName: "Retina Vitrectomy",
        branches: ["Newtown Branch", "Salt Lake Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "14",
        groupName: "Retina Silicone Oil Removal",
        branches: ["Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "15",
        groupName: "Retina Injection",
        branches: ["Park Street Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "16",
        groupName: "Retina Scleral Buckling",
        branches: ["Newtown Branch", "Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "17",
        groupName: "Retinal",
        branches: ["Salt Lake Branch", "Park Street Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "18",
        groupName: "Retina",
        branches: ["Newtown Branch", "Salt Lake Branch", "Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        _id: "19",
        groupName: "Retina (Vitrectomy)",
        branches: ["Park Street Branch", "Howrah Branch"],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];

    this.setState({ groups: dummyGroups });
  };

  editGroup = (group) => {
    // Open edit modal with prefilled values
    this.setState({
      isEditModalOpen: true,
      editingGroup: { ...group },
    });
  };

  updateGroupFromModal = async () => {
    const { editingGroup } = this.state;

    // Validation
    if (!editingGroup.groupName.trim()) {
      cogoToast.error("Group Name is required");
      return;
    }

    if (!editingGroup.branches.length) {
      cogoToast.error("At least one branch must be selected");
      return;
    }

    // Check for duplicate group names (case-sensitive, including whitespace and special characters)
    const isDuplicate = this.state.groups.some((existingGroup) => {
      // Skip the current group being edited
      if (existingGroup._id === editingGroup._id) {
        return false;
      }
      // Compare exact names including case, whitespace, and special characters
      return existingGroup.groupName === editingGroup.groupName.trim();
    });

    if (isDuplicate) {
      cogoToast.error(`Group name "${editingGroup.groupName.trim()}" already exists. Please choose a different name.`);
      return;
    }

    try {
      cogoToast.loading("Updating surgery group...");
      this.setState({ isSaving: true });

      const updatePayload = {
        _id: editingGroup._id,
        groupName: editingGroup.groupName.trim(),
        branches: editingGroup.branches,
        isShell: false,
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/update`,
        updatePayload
      );

      if (response && response._id) {
        const updatedGroup = {
          ...response,
          isShell: false,
        };

        this.setState((prevState) => ({
          groups: (prevState.groups || []).map((group) => (group._id === editingGroup._id ? updatedGroup : group)),
          isSaving: false,
          isEditModalOpen: false,
          editingGroup: null,
        }));

        // Log audit event for group update
        try {
          const auditResult = await AuditService.logEntityUpdate({
            entityType: "surgeryGroup",
            entityId: editingGroup._id,
            oldData: this.state.groups.find((g) => g._id === editingGroup._id),
            newData: updatedGroup,
            authStore: this.props.authStore,
            description: `Updated surgery group "${updatedGroup.groupName}"`,
          });
          console.log("✅ DEBUG: Audit log updated successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log for update", auditError);
        }

        cogoToast.success("Surgery group updated successfully!");
      } else {
        throw new Error("Invalid response from update API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error updating surgery group:", err);
      cogoToast.error("Failed to update surgery group. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  closeEditModal = () => {
    this.setState({
      isEditModalOpen: false,
      editingGroup: null,
    });
  };

  handleNewGroupSubmit = () => {
    const { newGroup } = this.state;

    if (!newGroup.groupName.trim()) {
      cogoToast.error("Please enter a group name");
      return;
    }

    if (!newGroup.sites.length) {
      cogoToast.error("Please select at least one site");
      return;
    }

    // Convert sites to branch names for the API payload
    const branchNames = newGroup.sites.map((site) => site.siteName || site);

    this.setState(
      {
        newGroup: {
          ...newGroup,
          branches: branchNames,
        },
      },
      () => {
        this.createNew();
      }
    );
  };

  createNew = async () => {
    this.setState({ isSaving: true });
    cogoToast.loading("Creating new Surgery Group Master");

    try {
      console.log("🔍 DEBUG: Creating new surgery group via API");

      // Create new group via API
      const newGroupData = {
        groupName: this.state.newGroup.groupName,
        branches: this.state.newGroup.branches,
        isShell: false,
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/createNew`,
        newGroupData
      );

      if (response && response._id) {
        const newGroup = {
          ...response,
          isShell: false,
        };

        // Log audit event for group creation
        console.log("🔍 DEBUG: Creating audit log for surgery group creation", {
          entityType: "surgeryGroup",
          entityId: newGroup._id,
          authStore: this.props.authStore,
          description: `Created new surgery group "${newGroup.groupName || "Unnamed Group"}"`,
        });

        try {
          const auditResult = await AuditService.logEntityCreate({
            entityType: "surgeryGroup",
            entityId: newGroup._id,
            newData: newGroup,
            authStore: this.props.authStore,
            description: `Created new surgery group "${newGroup.groupName || "Unnamed Group"}"`,
          });
          console.log("✅ DEBUG: Audit log created successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log", auditError);
        }

        this.setState({
          groups: [newGroup, ...this.state.groups],
          isSaving: false,
          showNewGroupModal: false,
          newGroup: {
            groupName: "",
            sites: [],
            branches: [],
          },
        });
        cogoToast.success("Surgery group created successfully! You can now edit it inline.");
      } else {
        throw new Error("Invalid response from create API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error creating surgery group:", err);
      cogoToast.error("Failed to create new surgery group. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  updateGroup = async (_id, updatedData) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Updating surgery group...");

    // Find the current group data for audit logging
    const currentGroup = this.state.groups.find((group) => group._id === _id);
    const oldData = currentGroup ? { ...currentGroup } : null;

    try {
      console.log("🔍 DEBUG: Updating surgery group via API", {
        _id,
        updatedData,
      });

      // Prepare update payload
      const updatePayload = {
        _id,
        ...updatedData,
        isShell: false,
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/update`,
        updatePayload
      );

      if (response && response._id) {
        const updatedGroup = {
          ...response,
          isShell: false,
        };

        this.setState((prevState) => ({
          groups: (prevState.groups || []).map((group) => (group._id === _id ? updatedGroup : group)),
          isSaving: false,
        }));

        // Log audit event for group update
        console.log("🔍 DEBUG: Creating audit log for surgery group update", {
          entityType: "surgeryGroup",
          entityId: _id,
          oldData: oldData,
          newData: updatedGroup,
          authStore: this.props.authStore,
          description: `Updated surgery group "${updatedGroup.groupName}"`,
        });

        try {
          const auditResult = await AuditService.logEntityUpdate({
            entityType: "surgeryGroup",
            entityId: _id,
            oldData: oldData,
            newData: updatedGroup,
            authStore: this.props.authStore,
            description: `Updated surgery group "${updatedGroup.groupName}"`,
          });
          console.log("✅ DEBUG: Audit log updated successfully", auditResult);
        } catch (auditError) {
          console.error("❌ DEBUG: Failed to create audit log for update", auditError);
        }

        cogoToast.success("Surgery group updated successfully!");
      } else {
        throw new Error("Invalid response from update API");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error updating surgery group:", err);
      cogoToast.error("Failed to update surgery group. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  showDeleteConfirmation = (group) => {
    this.setState({
      showDeleteConfirmation: true,
      groupToDelete: group,
    });
  };

  hideDeleteConfirmation = () => {
    this.setState({
      showDeleteConfirmation: false,
      groupToDelete: null,
    });
  };

  confirmDelete = () => {
    if (this.state.groupToDelete) {
      this.deleteGroup(this.state.groupToDelete._id);
      this.hideDeleteConfirmation();
    }
  };

  deleteGroup = async (_id) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Deleting surgery group...");

    // Find the group data for audit logging
    const groupToDelete = this.state.groups.find((group) => group._id === _id);

    try {
      console.log("🔍 DEBUG: Deleting surgery group via API", {
        _id,
      });

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxSurgeryGroupMaster/delete`, { _id });

      if (response && response.success !== false) {
        this.setState((prevState) => ({
          groups: (prevState.groups || []).filter((group) => group._id !== _id),
          isSaving: false,
        }));

        // Log audit event for group deletion
        if (groupToDelete) {
          console.log("🔍 DEBUG: Creating audit log for surgery group deletion", {
            entityType: "surgeryGroup",
            entityId: _id,
            deletedData: groupToDelete,
            authStore: this.props.authStore,
            description: `Deleted surgery group "${groupToDelete.groupName}"`,
          });

          try {
            const auditResult = await AuditService.logEntityDelete({
              entityType: "surgeryGroup",
              entityId: _id,
              deletedData: groupToDelete,
              authStore: this.props.authStore,
              description: `Deleted surgery group "${groupToDelete.groupName}"`,
            });
            console.log("✅ DEBUG: Audit log deleted successfully", auditResult);
          } catch (auditError) {
            console.error("❌ DEBUG: Failed to create audit log for deletion", auditError);
          }
        }

        cogoToast.success("Surgery group deleted successfully!");
      } else {
        throw new Error("Delete operation failed or was rejected");
      }
    } catch (err) {
      console.error("❌ DEBUG: Error deleting surgery group:", err);
      cogoToast.error("Failed to delete surgery group. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  logAudit = async (action, entityId, description, changes = {}) => {
    try {
      console.log("🔍 DEBUG: Creating audit log", {
        action,
        entityId,
        description,
        changes,
      });

      await AuditService.createAuditLog(
        this.props.authStore.prefix,
        "surgeryGroup",
        entityId,
        action,
        description,
        changes
      );

      console.log("✅ DEBUG: Audit log created successfully");
    } catch (error) {
      console.error("❌ DEBUG: Failed to create audit log:", error);
    }
  };

  handleShowAuditLogs = () => {
    this.setState({ showAuditLogs: true });
  };

  handleCloseAuditLogs = () => {
    this.setState({ showAuditLogs: false });
  };

  renderHeader = () => {
    const { quickFilterText, isSaving, groups, branches } = this.state;

    return (
      <div style={{ marginBottom: "var(--v3-spacing-lg)" }}>
        <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Surgery Group Master</H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "var(--v3-spacing-xs) 0",
            fontSize: "var(--v3-font-size-md)",
            fontFamily: "var(--v3-font-family-sans)",
          }}
        >
          Manage surgery groups and their associated branch networks
        </Text>

        <Card elevation={Elevation.TWO} style={{ padding: "var(--v3-spacing-lg)", marginTop: "var(--v3-spacing-lg)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--v3-spacing-md)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
              <div style={{ minWidth: "320px" }}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search surgery groups or branches..."
                  value={quickFilterText}
                  onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                  disabled={isSaving}
                />
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <Tag large intent="primary" icon="numerical">
                  {groups.length} Groups
                </Tag>
                <Tag large intent="success" icon="map-marker">
                  {branches.length} Branches
                </Tag>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Tooltip content="Refresh data" position={Position.BOTTOM}>
                <Button
                  icon="refresh"
                  disabled={isSaving}
                  onClick={() => {
                    this.loadBranches();
                    this.loadGroups();
                    this.loadSites();
                  }}
                />
              </Tooltip>

              <Tooltip content="View audit logs" position={Position.BOTTOM}>
                <Button icon="history" disabled={isSaving} onClick={this.handleShowAuditLogs} />
              </Tooltip>

              <Button
                icon="plus"
                intent="primary"
                disabled={isSaving}
                onClick={() => this.setState({ showNewGroupModal: true })}
              >
                New Surgery Group
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  renderTable = () => {
    const { groups, quickFilterText, isLoading } = this.state;

    if (!groups.length) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "var(--v3-spacing-lg)" }}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <H3
              style={{
                color: "var(--text-primary)",
                marginBottom: "var(--v3-spacing-lg)",
                fontSize: "var(--v3-font-size-xl)",
                fontWeight: "var(--v3-font-weight-semibold)",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              {quickFilterText ? "No matching surgery groups found" : "No surgery groups configured"}
            </H3>
            <Text
              style={{
                color: "var(--text-secondary)",
                marginBottom: "var(--v3-spacing-xl)",
                fontSize: "var(--v3-font-size-md)",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              {quickFilterText
                ? "Try adjusting your search terms or clear the search to view all surgery groups."
                : "Get started by creating your first surgery group master."}
            </Text>
            {quickFilterText ? (
              <Button icon="cross" intent="primary" onClick={() => this.setState({ quickFilterText: "" })}>
                Clear Search
              </Button>
            ) : (
              <Button
                icon="plus"
                intent="primary"
                large
                onClick={() => this.setState({ showNewGroupModal: true })}
                loading={this.state.isSaving}
              >
                Create First Surgery Group
              </Button>
            )}
          </div>
        </Card>
      );
    }

    return (
      <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
        <div className="ag-theme-alpine-dark" style={{ height: "500px", width: "100%" }}>
          <AgGridReact
            columnDefs={this.columnDefs}
            rowData={groups}
            onGridReady={this.onGridReady}
            quickFilterText={quickFilterText}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            rowSelection="single"
            enableRangeSelection={true}
            enableCharts={true}
            sideBar={true}
            statusBar={{
              statusPanels: [
                {
                  statusPanel: "agTotalAndFilteredRowCountComponent",
                  align: "left",
                },
              ],
            }}
            loading={isLoading}
          />
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.showAuditLogs) {
      return <AuditLogViewer onClose={this.handleCloseAuditLogs} />;
    }

    if (this.state.isLoading) {
      return (
        <div
          className="bp3-dark"
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
            minHeight: "100vh",
          }}
        >
          <NonIdealState
            icon={<Spinner size={50} />}
            title="Loading Surgery Group Masters"
            description="Please wait while we load your surgery group data and branch configurations..."
          />
        </div>
      );
    }

    return (
      <div
        className="bp3-dark"
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {this.renderHeader()}
        {this.renderTable()}
        {this.renderNewGroupModal()}
        {this.renderEditModal()}
        {this.renderDeleteConfirmation()}
      </div>
    );
  }

  renderEditModal = () => {
    const { isEditModalOpen, editingGroup, branches, isSaving } = this.state;

    if (!isEditModalOpen || !editingGroup) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={this.closeEditModal}
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Edit Surgery Group</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Update the surgery group name and branch assignments.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Group Name *
              </Tag>
              <InputGroup
                placeholder="Enter surgery group name..."
                value={editingGroup.groupName}
                onChange={(e) =>
                  this.setState({
                    editingGroup: { ...editingGroup, groupName: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={!editingGroup.groupName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "300px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={editingGroup.branches}
                    onChange={(selectedBranches) =>
                      this.setState({
                        editingGroup: { ...editingGroup, branches: selectedBranches },
                      })
                    }
                    placeholder="Select branches for this group..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button large intent="none" icon="cross" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.updateGroupFromModal}
              loading={isSaving}
              disabled={isSaving || !editingGroup.groupName.trim() || !editingGroup.branches.length}
            >
              Update Group
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNewGroupModal = () => {
    const { showNewGroupModal, newGroup, sites, isSaving } = this.state;

    if (!showNewGroupModal) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={() =>
          this.setState({
            showNewGroupModal: false,
            newGroup: { groupName: "", sites: [], branches: [] },
          })
        }
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Create New Surgery Group</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Enter the surgery group name and select the sites where this group will be available.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Group Name *
              </Tag>
              <InputGroup
                placeholder="Enter surgery group name..."
                value={newGroup.groupName}
                onChange={(e) =>
                  this.setState({
                    newGroup: { ...newGroup, groupName: e.target.value },
                  })
                }
                disabled={isSaving}
                intent={!newGroup.groupName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "300px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Sites *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={sites}
                    selectedItems={newGroup.sites}
                    onChange={(selectedSites) =>
                      this.setState({
                        newGroup: { ...newGroup, sites: selectedSites },
                      })
                    }
                    placeholder="Select sites for this group..."
                    label="Sites"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No sites available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button
              large
              intent="none"
              icon="cross"
              onClick={() =>
                this.setState({
                  showNewGroupModal: false,
                  newGroup: { groupName: "", sites: [], branches: [] },
                })
              }
            >
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.handleNewGroupSubmit}
              loading={isSaving}
              disabled={isSaving || !newGroup.groupName.trim() || !newGroup.sites.length}
            >
              Create Group
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderDeleteConfirmation = () => {
    const { showDeleteConfirmation, groupToDelete } = this.state;

    return (
      <Alert
        isOpen={showDeleteConfirmation}
        intent={Intent.DANGER}
        icon="trash"
        onCancel={this.hideDeleteConfirmation}
        onConfirm={this.confirmDelete}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      >
        <p>
          Are you sure you want to delete the surgery group{" "}
          <strong>"{groupToDelete?.groupName}"</strong>?
        </p>
        <p>
          This action cannot be undone and will permanently remove this surgery group
          from the system.
        </p>
        {groupToDelete?.branches && groupToDelete.branches.length > 0 && (
          <p style={{ marginTop: "10px", fontSize: "14px", color: "var(--text-secondary)" }}>
            <strong>Associated branches:</strong> {groupToDelete.branches.join(", ")}
          </p>
        )}
      </Alert>
    );
  };
}

export default inject("authStore")(observer(SurgeryGroupMaster));
